import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { DollarSign } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentStatus = () => {
  const [paymentData, setPaymentData] = useState({
    outstanding: [],
    paid: []
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('outstanding');

  useEffect(() => {
    fetchPaymentStatus();
  }, []);

  const fetchPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API}/clients/payment-status`);
      setPaymentData(response.data);
    } catch (error) {
      console.error('Error fetching receipt status:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'overdue':
        return 'bg-gradient-to-r from-red-500 to-pink-600 text-white';
      case 'due':
        return 'bg-gradient-to-r from-yellow-500 to-orange-600 text-white';
      case 'paid':
        return 'bg-gradient-to-r from-green-500 to-emerald-600 text-white';
      default:
        return 'bg-gradient-to-r from-gray-500 to-slate-600 text-white';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'overdue':
        return <XCircle className="w-4 h-4" />;
      case 'due':
        return <AlertCircle className="w-4 h-4" />;
      case 'paid':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-red-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-400 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading receipt status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-red-50">
      {/* Header Section */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-orange-100/50 px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-2">
                Receipt Status Dashboard
              </h1>
              <p className="text-gray-600 text-lg">Monitor client fee receipts and outstanding balances at a glance</p>
            </div>
            <div className="mt-4 md:mt-0 flex items-center space-x-4">
              <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-6 py-3 rounded-2xl shadow-lg">
                <div className="text-sm font-medium">Total Outstanding</div>
                <div className="text-2xl font-bold">
                  ₹{paymentData.outstanding.reduce((sum, item) => sum + item.amount, 0).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto space-y-8">
        {/* Status Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="bg-gradient-to-br from-red-500 to-pink-600 text-white border-0 shadow-xl">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold mb-2">
                {paymentData.outstanding.filter(p => p.status === 'overdue').length}
              </div>
              <div className="text-red-100">Overdue Receipts</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-yellow-500 to-orange-600 text-white border-0 shadow-xl">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold mb-2">
                {paymentData.outstanding.filter(p => p.status === 'due').length}
              </div>
              <div className="text-yellow-100">Due Receipts</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0 shadow-xl">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold mb-2">
                {paymentData.paid.length}
              </div>
              <div className="text-green-100">Received This Month</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white border-0 shadow-xl">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold mb-2">
                ₹{paymentData.paid.reduce((sum, item) => sum + item.amount, 0).toLocaleString()}
              </div>
              <div className="text-purple-100">Total Received</div>
            </CardContent>
          </Card>
        </div>

        {/* Tabbed Interface */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-2xl">
          <CardHeader className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl font-bold flex items-center">
                <CreditCard className="w-6 h-6 mr-3" />
                Receipt Status Overview
              </CardTitle>
              <div className="flex space-x-2">
                <Button 
                  variant={activeTab === 'outstanding' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('outstanding')}
                  className={activeTab === 'outstanding' ? 'bg-white text-emerald-600' : 'border-white text-white hover:bg-white hover:text-emerald-600'}
                >
                  Outstanding ({paymentData.outstanding.length})
                </Button>
                <Button 
                  variant={activeTab === 'paid' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('paid')}
                  className={activeTab === 'paid' ? 'bg-white text-emerald-600' : 'border-white text-white hover:bg-white hover:text-emerald-600'}
                >
                  Paid ({paymentData.paid.length})
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-orange-100">
                    <TableHead className="font-semibold text-gray-700">Client</TableHead>
                    <TableHead className="font-semibold text-gray-700">Service</TableHead>
                    <TableHead className="font-semibold text-gray-700">Amount</TableHead>
                    <TableHead className="font-semibold text-gray-700">Due Date</TableHead>
                    <TableHead className="font-semibold text-gray-700">Status</TableHead>
                    <TableHead className="font-semibold text-gray-700">Days {activeTab === 'outstanding' ? 'Overdue' : 'Since Paid'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(activeTab === 'outstanding' ? paymentData.outstanding : paymentData.paid).map((payment, index) => (
                    <TableRow key={index} className="hover:bg-gray-50">
                      <TableCell>
                        <div>
                          <div className="font-medium text-gray-900">{payment.client_name}</div>
                          <div className="text-sm text-gray-500">{payment.organization_name}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-gray-900">{payment.service_name}</div>
                        <div className="text-sm text-gray-500">{payment.billing_frequency}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-lg font-bold text-gray-900">₹{payment.amount.toLocaleString()}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-gray-900">{new Date(payment.due_date).toLocaleDateString()}</div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getStatusColor(payment.status)} flex items-center space-x-1`}>
                          {getStatusIcon(payment.status)}
                          <span>{payment.status.toUpperCase()}</span>
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className={`font-medium ${payment.days_overdue > 30 ? 'text-red-600' : payment.days_overdue > 7 ? 'text-yellow-600' : 'text-gray-600'}`}>
                          {payment.days_overdue} days
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentStatus;
